// prettier-ignore
import {
   defaultStats, IStats,
   PLAYER_FRAME, STARTING_COLS, STARTING_LEVEL, TILE_WIDTH, IStat
} from "../../data/settings";
import { SceneSetters, Container, Sprite } from "../../types";
import { addCard, addRewards, setupDrag, fromGrid } from "./helpers/cardHelper";
import { reverseText, updateStat, showLevelText } from "./helpers/textHelper";
import { generateAnimations, play } from "./helpers/animationHelper";
import { wiggle } from "./helpers/tweenHelper";
import { addCards } from "./helpers/levelCardHelper";

const sceneConfig: Phaser.Types.Scenes.SettingsConfig = {
  active: false,
  visible: false,
  key: "Game",
};

type IPoint = [number, number];
type IPath = IPoint[];
type GameSceneData = { level: number; stats: IStats };

export default class GameScene extends Phaser.Scene {
  /* eslint-disable @typescript-eslint/lines-between-class-members */
  private setter: SceneSetters;
  private level: number;
  private path: IPath;
  private fow?: Container;
  private fowAlpha: number;
  private animationSprite?: Sprite;
  /* eslint-enable @typescript-eslint/lines-between-class-members */

  constructor(setter: SceneSetters) {
    super(sceneConfig);
    this.setter = setter;
    this.level = STARTING_LEVEL;
    this.path = [];
    this.fowAlpha = 0.6;
  }

  public preload() {
    this.load.atlas(
      "atlas",
      `${process.env.PUBLIC_URL}/spritesheet.png`,
      `${process.env.PUBLIC_URL}/spritesheet.json`
    );
    this.load.audio("main_track", `${process.env.PUBLIC_URL}/music/main_track.mp3`);
    this.load.audio("wand_effect", `${process.env.PUBLIC_URL}/music/wand.wav`);
    this.load.audio("success_effect", `${process.env.PUBLIC_URL}/music/success.wav`);
    // this.load.glsl("smoke", `${process.env.PUBLIC_URL}/smoke.glsl`);
  }

  public create(data: GameSceneData) {
    // setup level, path
    this.path = [];
    this.level = data.level ?? STARTING_LEVEL;
    const stats = data.stats ?? defaultStats;

    // setup the cards
    const mainCard = addCard(this, PLAYER_FRAME, 0, 1, stats).setDepth(1);
    this.setupDrag(mainCard);
    addCards(this, this.getCols(), this.level);
    addRewards(this, this.level);

    // add fog of war on top of cards
    this.fow = this.addFow();

    // re-center the camera
    this.cameras.main.centerOn(mainCard.x + 2 * TILE_WIDTH, 1.5 * TILE_WIDTH);
    this.animationSprite = generateAnimations(this);
    if (this.level > 0) showLevelText(this, this.level);

    // play main theme (if it isn't playing already)
    const mainTrack = this.sound.get("main_track");
    if (!mainTrack) this.sound.play("main_track", { volume: 0.4, loop: true });
  }

  private addFow() {
    const cols = this.getCols();
    const innerRectangles = [
      [0, 0, 1, 3],
      [2, 0, cols, 3],
      [-(1 + cols), 0, cols, 3],
    ].map(([x, y, w, h]) => {
      return this.add
        .rectangle(TILE_WIDTH * x, TILE_WIDTH * y, TILE_WIDTH * w, TILE_WIDTH * h, 0x2b2b2b)
        .setOrigin(0);
    });
    // const shader = this.add
    //   .shader("smoke", TILE_WIDTH * 2, 0, cols * TILE_WIDTH, 3 * TILE_WIDTH)
    //   .setOrigin(0);
    return this.add.container(0, 0, innerRectangles).setAlpha(this.fowAlpha); // .add(shader);
  }

  private setupDrag(card: Container) {
    // eslint-disable-next-line complexity
    const onDragEnd = (
      gameObject: Container,
      nextPoint: IPoint,
      initialPoint: IPoint,
      gridPoint: IPoint
    ) => {
      const [x, y] = nextPoint;
      const [initialX, initialY] = initialPoint;
      const [gridX, gridY] = gridPoint;

      // return early if the card shouldn't move, reset it to its initial position
      const validMove = Math.abs(x - initialX) === TILE_WIDTH;
      const isRewinding = x < initialX;
      const hasMPLeft = isRewinding || card.data.values?.mp[0] > 0;
      const outOfBounds = gridY < 0 || gridY > 2 || gridX <= 0;
      if (!validMove || !hasMPLeft || outOfBounds) {
        [gameObject.x, gameObject.y] = [initialX, initialY];
        return undefined;
      }

      // handle path
      // if going forward, push path and reverse card at initial position
      // if rewinding, check that we match the last path point. if so, pop path and reverse card back to its original form
      if (isRewinding) {
        const [lastX, lastY] = this.path[this.path.length - 1] ?? [];
        // last path doesn't match new point so just reset card to its initial position
        if (x !== lastX || y !== lastY) {
          [gameObject.x, gameObject.y] = [initialX, initialY];
          return undefined;
        }
        const point = this.path.pop();
        if (point) this.reverseCardAt(point);
      } else {
        this.path.push([initialX, initialY]);
        this.reverseCardAt([initialX, initialY], 0xffffff);
      }

      [gameObject.x, gameObject.y] = [x, y];
      play(gameObject, "flash", this.animationSprite);
      wiggle(this, card);

      // add a line between the points you've just gone through
      this.add
        .line(0, 0, initialX, initialY, x, y, 0xffffff, 0.3)
        .setDepth(-1)
        .setOrigin(0)
        .setLineWidth(5);

      // re-center the camera and update fog of war (now using tweens)
      this.cameras.main.pan(x + TILE_WIDTH, 1.5 * TILE_WIDTH, 1500, "Circ", true);
      this.tweens.add({
        targets: this.fow,
        x: x - TILE_WIDTH / 2,
        duration: 500,
        ease: "Circ",
      });

      this.updateStatsAt(card, [gridX, gridY], isRewinding);
      return undefined;
    };
    setupDrag(this, card, onDragEnd);
    return card;
  }

  // eslint-disable-next-line complexity
  private updateStatsAt(card: Container, point: IPoint, isRewinding: boolean) {
    const [gridX, gridY] = point;
    const otherCard = this.children.getFirst("name", `${gridX},${gridY}`);
    if (!otherCard) return undefined;

    const { hp = 0, mp = 0, coin = 0, type, subtype } = otherCard.data.values;
    this.sound.play(type === "reward" ? "success_effect" : "wand_effect", { volume: 1 });
    if (type === "reward") {
      const stats: IStats = this.updateStats(card, subtype);
      if (gridX === this.getCols() + 1) this.goToNextLevel(stats);
      return undefined;
    }
    const modifier = isRewinding ? -1 : 1;
    const { coin: [oldCoinStat] = [0, 0] } = card.data.values;
    this.setter.setCoin((value) => updateStat(card, "coin", value + coin));
    // if you are buying a card, you spent coin so that hp & mp modifiers are always positive
    // key important detail: make sure oldCoinStat is at least 1. Otherwise, having no money would let you buy anything
    const isBuying = coin < 0 && oldCoinStat + coin >= 0 && oldCoinStat > 0;

    this.setter.setHP((value) =>
      updateStat(card, "hp", value + (isBuying ? Math.abs(hp) : hp * modifier))
    );
    this.setter.setMP((value) =>
      updateStat(card, "mp", value + (isBuying ? Math.abs(mp) : mp * modifier))
    );
    return undefined;
  }

  // eslint-disable-next-line complexity
  private updateStats(card: Phaser.GameObjects.Container, subtype: any) {
    const { hp, mp, coin, level } = card.data.values;
    const stats: IStats = { hp: [...hp] as IStat, mp: [...mp] as IStat, coin, level };
    // @ts-ignore
    const [hpValue, hpMax] = stats.hp;
    // @ts-ignore
    const [mpValue, mpMax] = stats.mp;
    // @ts-ignore
    const [coinValue, coinMax] = stats.coin ?? [0, 0];
    switch (subtype) {
      // fully recover
      case "campfire":
        stats.hp = [hpMax, hpMax];
        stats.mp = [mpMax, mpMax];
        break;
      // recover HP to its max
      case "meat":
        stats.hp = [hpMax, hpMax];
        break;
      // recover MP to its max
      case "fish":
        stats.mp = [mpMax, mpMax];
        break;
      // swap hp and mp, fully recover them
      case "nautilus":
        stats.hp = [mpMax, mpMax];
        stats.mp = [hpMax, hpMax];
        break;
      // increase HP +1
      case "red_amulet":
        stats.hp = [hpValue, hpMax + 1];
        break;
      // increase MP +5
      case "blue_amulet":
        stats.mp = [mpValue, mpMax + 1];
        break;
      // hide map
      case "scroll":
        this.fowAlpha = 1;
        this.fow?.setAlpha(1);
        break;
      // show map
      case "astrolabe":
        this.fowAlpha = 0.4;
        this.fow?.setAlpha(0.4);
        break;
      // +25 gold
      case "gold_pot":
        stats.coin = [coinValue + 25, coinMax];
        break;
      // MP set to infinity
      case "dream_catcher":
        stats.mp = [mpValue, Infinity];
        break;
      // add item to drops (diamond, emerald, nut, pinecone and even crystal_ball)
      default: {
        this.setter.setDrops((dic) => ({ ...dic, [subtype]: (dic[subtype] ?? 0) + 1 }));
        break;
      }
    }
    // @ts-ignore
    this.setter.setHP(() => updateStat(card, "hp", stats.hp[0]));
    // @ts-ignore
    this.setter.setMP(() => updateStat(card, "mp", stats.mp[0]));
    // @ts-ignore
    this.setter.setCoin(() => updateStat(card, "coin", stats.coin[0]));
    return stats;
  }

  private reverseCardAt(point: IPoint, color?: number) {
    const [gridX, gridY] = point.map(fromGrid);
    const prevCard = this.children.getFirst("name", `${gridX},${gridY}`) as Container;
    if (!prevCard || gridX <= 0) return;

    // set (or clear) tint of card at given point
    const bgSprite = prevCard.getByName("bg") as Phaser.GameObjects.Sprite;
    if (color) bgSprite.setTintFill(color).setAlpha(0.95);
    else bgSprite.clearTint();

    // reverse the stats text (multiply by -1)
    reverseText(prevCard.getByName("hp") as Phaser.GameObjects.Text);
    reverseText(prevCard.getByName("mp") as Phaser.GameObjects.Text);
    // note: we explicitly DO NOT reverse coin
  }

  private getCols() {
    return this.level * 2 + STARTING_COLS;
  }

  private goToNextLevel(stats: Record<string, any>) {
    this.setter.setLevel((currentLevel) => currentLevel + 1);
    this.scene.restart({ level: this.level + 1, stats });
  }
}
